// Embedded "Propose a Reviewer" form — COI-FIRST (July-2 FR-J12). The ordered walls run
// server-side BEFORE the proposal exists: author check → duplicate/recent → same-affiliation
// (requires the Backup flag) → Prophy COI (stubbed pass). July-8 FR-L3: a reviewer that passes
// is created INCOMPLETE and the Part-2 reviewer-profile form opens immediately — only its
// submission puts the proposal in front of AE Quality Check.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ProposalView } from "@rrid/shared-types";
import { api, ApiError } from "../../lib/api.js";
import { Badge, Button, Field, Input } from "../../components/ui.js";
import { EvaluateModal } from "../reviewers/EvaluateModal.js";

interface Step1 {
  firstName: string;
  lastName: string;
  email: string;
  affiliation: string;
  inviteBackup: boolean;
  affiliationCoi: boolean;
}
const EMPTY: Step1 = { firstName: "", lastName: "", email: "", affiliation: "", inviteBackup: false, affiliationCoi: false };

interface WallError {
  error: string;
  wall: "author" | "duplicate" | "recent" | "affiliation";
  requireBackup?: boolean;
}

export function ProposeReviewer({ selectedId, canPropose }: { selectedId: string; canPropose: boolean }): JSX.Element {
  const [f, setF] = useState<Step1>(EMPTY);
  const [submitted, setSubmitted] = useState<ProposalView | null>(null);
  // FR-L3: the Part-2 form opens right after a successful propose.
  const [evaluating, setEvaluating] = useState<ProposalView | null>(null);
  const [wallError, setWallError] = useState<WallError | null>(null);
  const qc = useQueryClient();

  const submit = useMutation({
    mutationFn: () => api.post<{ proposal: ProposalView }>(`/preprints/selected/${selectedId}/propose-reviewer`, f),
    onSuccess: ({ proposal }) => {
      setSubmitted(proposal);
      setEvaluating(proposal);
      setWallError(null);
      setF(EMPTY);
      qc.invalidateQueries({ queryKey: ["proposals"] });
    },
    onError: (err) => {
      // A 409 is a COI wall block: show the reason (and the Backup hint for wall 3).
      if (err instanceof ApiError && err.status === 409 && err.body && typeof err.body === "object") {
        setWallError(err.body as WallError);
      } else {
        setWallError({ error: "Could not create the proposal. Check the fields and try again.", wall: "author" });
      }
    },
  });

  if (!canPropose) return <p className="text-xs text-slate-500">You don’t have access to propose reviewers for this preprint.</p>;

  if (submitted) {
    return (
      <div className="space-y-3">
        <div className="rounded-md border border-green-200 bg-green-50 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="green">COI passed</Badge>
            <span className="text-sm font-medium">{submitted.firstName} {submitted.lastName}</span>
            <span className="text-xs text-slate-500">{submitted.reviewerEmail}</span>
          </div>
          <p className="mt-1 text-xs text-slate-600">
            Proposal created — complete the reviewer profile (Part 2) to send it to quality check.
          </p>
          {!evaluating && (
            <Button className="mt-2" variant="secondary" onClick={() => setEvaluating(submitted)}>
              Complete reviewer profile
            </Button>
          )}
        </div>
        <Button variant="ghost" onClick={() => setSubmitted(null)}>+ Propose another reviewer</Button>
        {evaluating && <EvaluateModal proposal={evaluating} onClose={() => setEvaluating(null)} />}
      </div>
    );
  }

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        submit.mutate();
      }}
    >
      <div className="grid gap-2 sm:grid-cols-2">
        <Field label="Reviewer first name *">
          <Input value={f.firstName} onChange={(e) => setF({ ...f, firstName: e.target.value })} required />
        </Field>
        <Field label="Reviewer last name *">
          <Input value={f.lastName} onChange={(e) => setF({ ...f, lastName: e.target.value })} required />
        </Field>
        <Field label="Reviewer email *">
          <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
        </Field>
        <Field label="Affiliation *">
          <Input value={f.affiliation} onChange={(e) => setF({ ...f, affiliation: e.target.value })} required />
        </Field>
      </div>
      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input type="checkbox" checked={f.inviteBackup} onChange={(e) => setF({ ...f, inviteBackup: e.target.checked })} />
          Backup Reviewer?
        </label>
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input type="checkbox" checked={f.affiliationCoi} onChange={(e) => setF({ ...f, affiliationCoi: e.target.checked })} />
          Affiliation conflict of interest?
        </label>
      </div>
      {wallError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700" role="alert">
          <p>{wallError.error}</p>
          {wallError.requireBackup && (
            <p className="mt-1 font-medium">Tick “Backup Reviewer?” above to propose them as a backup.</p>
          )}
        </div>
      )}
      <Button type="submit" disabled={submit.isPending}>
        {submit.isPending ? "Running COI checks…" : "Propose Reviewer"}
      </Button>
    </form>
  );
}
